import { createSlice } from '@reduxjs/toolkit';
import { signinUser, logoutAdmin, logoutUser } from '~/api';
import { registerUser, registerAdmin, signinAdmin } from '~/apiTs';
import i18n from '~/locales/i18next';

import {
  SURVEY_STATUSES,
  LIMIT_TOTAL_AMOUNT,
  LOCALE_STRING,
  STATIC_URLS,
  LIMIT_TARGET_SAMPLES_TOTAL,
  REJECT_REASON_MAX_LENGTH,
} from '~/constants';
import { GMOASK_STATUS, ERROR_CODE_MESSAGE_MAP } from '~/constants/api';
import { validateUtf8mb4 } from '~/constants/validationRules';

const initialState = {
  signin: {
    errors: {
      authentication: {
        invalid: false,
        message: '',
      },
    },
  },

  signup: {
    errors: {
      invalid: false,
      message: '',
    },
  },

  changeSurveyStatus: {
    status: {
      valid: false,
      invalid: false,
    },
    errors: {
      surveyStatusId: {
        invalid: false,
        message: '',
      },
      rejectReason: {
        invalid: false,
        message: '',
      },
      rejectCategory: {
        invalid: false,
        message: '',
      },
    },
  },

  clickedSearchDeliveryTarget: {
    status: {
      valid: false,
      invalid: false,
    },
    errors: {
      qualifications: {
        age: {
          invalid: false,
          message: '',
        },
        gender: {
          invalid: false,
          message: '',
        },
      },
    },
  },

  clickedGoToApply: {
    status: {
      valid: false,
      invalid: false,
      isSubmitBtnClicked: false,
    },
    errors: {
      surveyPrice: {
        invalid: false,
        message: '',
        href: '',
      },
      qualifications: {
        age: {
          invalid: false,
          message: '',
        },
        gender: {
          invalid: false,
          message: '',
        },
      },
      quotas: {
        quotas: {
          invalid: false,
          message: '',
        },
        targetSamples: {
          invalid: false,
          message: '',
        },
      },
    },
  },
};

const isInvalid = (errors) => {
  return Object.keys(errors).some((category) => {
    if (errors[category].invalid === undefined) {
      return isInvalid(errors[category]);
    }
    return errors[category].invalid;
  });
};

const validateSurveyStatusChange = (data) => {
  // バリデーションエラーの初期値
  let validationErrors = { ...initialState.changeSurveyStatus.errors };

  // surveyStatusIdが未選択ならエラー
  if (data.surveyStatusId === null) {
    validationErrors = {
      ...validationErrors,
      surveyStatusId: {
        message: i18n.t('validation.requiredChoice'),
        invalid: true,
      },
    };
  }

  // surveyStatusIdが7:却下の場合
  if (data.surveyStatusId === SURVEY_STATUSES.REJECTED) {
    // rejectReasonが未入力ならエラー
    if (data.rejectReason === '') {
      validationErrors = {
        ...validationErrors,
        rejectReason: {
          message: i18n.t('validation.required'),
          invalid: true,
        },
      };
    }

    // 使用禁止文字 Utf8mb4が含まれていたらエラー
    if (!validateUtf8mb4(data.rejectReason)) {
      validationErrors = {
        ...validationErrors,
        rejectReason: {
          message: i18n.t('validation.includeUtf8mb4'),
          invalid: true,
        },
      };
    }

    // 3000文字超過ならエラー
    // maxlengthに3000が指定されているが、念のため。
    if (data.rejectReason.length > REJECT_REASON_MAX_LENGTH) {
      validationErrors = {
        ...validationErrors,
        rejectReason: {
          message: i18n.t('validation.maxLengthText', {
            length: REJECT_REASON_MAX_LENGTH,
          }),
          invalid: true,
        },
      };
    }

    // 却下理由が1つも選択されていないならエラー
    let isSelected = false;
    Object.keys(data.checkedRejectCategories).forEach((id) => {
      if (data.checkedRejectCategories[id]) {
        isSelected = true;
      }
    });
    if (!isSelected) {
      validationErrors = {
        ...validationErrors,
        rejectCategory: {
          message: i18n.t('validation.requiredChoice'),
          invalid: true,
        },
      };
    }
  }

  return validationErrors;
};

const validateClickGoToApply = (payload) => {
  const { estimatePriceTotal, targetSamplesTotal, qualifications, quotas: quotasSlice } = payload;
  const { quotas, targetSamples } = quotasSlice;
  const { age, gender } = qualifications;

  let validationErrorsSurveyPrice = {
    invalid: false,
    message: '',
    href: '',
  };

  let validationErrorsQualifications = {
    age: {
      invalid: false,
      message: '',
    },
    gender: {
      invalid: false,
      message: '',
    },
  };

  let validationErrorsQuotas = {
    quotas: {
      invalid: false,
      message: '',
    },
    targetSamples: {
      invalid: false,
      message: '',
    },
  };

  // 合計金額: LIMIT_TOTAL_AMOUNT範囲内
  const minAmount = LIMIT_TOTAL_AMOUNT.MIN;
  const maxAmount = LIMIT_TOTAL_AMOUNT.MAX;
  // 回答希望数が0の時は金額が0なので足りない金額はminAmountすべてになる
  if (targetSamplesTotal === 0) {
    validationErrorsSurveyPrice = {
      message: i18n.t('validation.lessNumber', {
        less: minAmount.toLocaleString(LOCALE_STRING.ja),
      }),
      invalid: true,
      href: '',
    };
  } else if (estimatePriceTotal < minAmount) {
    const less = minAmount - estimatePriceTotal;
    validationErrorsSurveyPrice = {
      message: i18n.t('validation.lessNumber', { less: less.toLocaleString(LOCALE_STRING.ja) }),
      invalid: true,
      href: '',
    };
  } else if (estimatePriceTotal > maxAmount) {
    validationErrorsSurveyPrice = {
      message: i18n.t('validation.aboveNumber'),
      invalid: true,
      href: STATIC_URLS.ESTIMATE_LIMIT,
    };
  }

  // 年齢: 必須
  if (age.from == null || age.to == null) {
    validationErrorsQualifications = {
      ...validationErrorsQualifications,
      age: {
        message: i18n.t('validation.requiredChoice'),
        invalid: true,
      },
    };
  }

  // 性別: 必須
  if (gender == null) {
    validationErrorsQualifications = {
      ...validationErrorsQualifications,
      gender: {
        message: i18n.t('validation.requiredChoice'),
        invalid: true,
      },
    };
  }

  // 割付: 必須 性別年齢が空の時も割付エラーを出す
  if (Object.keys(quotas).length === 0 || gender == null || age.from == null) {
    validationErrorsQuotas = {
      ...validationErrorsQuotas,
      quotas: {
        message: i18n.t('validation.notExist', {
          target: i18n.t('quotas.targetSamplesTotalBreakdown'),
        }),
        invalid: true,
      },
    };
  }

  // 回答希望数: 全て1以上
  if (
    Object.keys(quotas).length > 0 &&
    Object.keys(targetSamples).some((key) => targetSamples[key] === 0)
  ) {
    validationErrorsQuotas = {
      ...validationErrorsQuotas,
      targetSamples: {
        message: i18n.t('validation.targetSamplesIncludeZero'),
        invalid: true,
      },
    };
  }
  // 回答希望数: 提供可能数より多いものが存在する（履歴から復帰直後）
  if (
    Object.keys(quotasSlice.quotas).length > 0 &&
    Object.keys(targetSamples).some(
      (key) => targetSamples[key] > quotasSlice.quotas[key].minCollect
    )
  ) {
    validationErrorsQuotas = {
      ...validationErrorsQuotas,
      targetSamples: {
        message: i18n.t('validation.targetSampleIsLargerThanMinCollect'),
        invalid: true,
      },
    };
  }

  // 回答希望数合計: 99999問以内 (回答希望数合計は設定時保存時にバリデーションで弾かれるが、念のため)
  if (Number(targetSamplesTotal) > LIMIT_TARGET_SAMPLES_TOTAL.MAX) {
    validationErrorsQuotas = {
      ...validationErrorsQuotas,
      targetSamples: {
        message: i18n.t('validation.targetSampleTotalLessThanMax', {
          max: LIMIT_TARGET_SAMPLES_TOTAL.MAX.toLocaleString(LOCALE_STRING.ja),
        }),
        invalid: true,
      },
    };
  }

  return {
    surveyPrice: validationErrorsSurveyPrice,
    qualifications: validationErrorsQualifications,
    quotas: validationErrorsQuotas,
  };
};

const validateClickedSearchDeliveryTarget = (payload) => {
  const { age, gender } = payload.qualifications;

  let validationErrorsQualifications = {
    age: {
      invalid: false,
      message: '',
    },
    gender: {
      invalid: false,
      message: '',
    },
  };

  // 年齢: 必須
  if (age.from == null || age.to == null) {
    validationErrorsQualifications = {
      ...validationErrorsQualifications,
      age: {
        message: i18n.t('validation.requiredChoice'),
        invalid: true,
      },
    };
  }

  // 性別: 必須
  if (gender == null) {
    validationErrorsQualifications = {
      ...validationErrorsQualifications,
      gender: {
        message: i18n.t('validation.requiredChoice'),
        invalid: true,
      },
    };
  }

  return {
    qualifications: validationErrorsQualifications,
  };
};

/**
 * バリデーションの状態管理Slice。
 */
const validationSlice = createSlice({
  name: 'validation',
  initialState,
  reducers: {
    /**
     * 完了したらvalidationの状態を初期化する
     */
    validationInitialized: (state, action) => {
      switch (action.payload.eventType) {
        case 'signup':
          return {
            ...state,
            signup: initialState.signup,
          };

        case 'signin':
          return {
            ...state,
            signin: initialState.signin,
          };

        case 'changeSurveyStatus': {
          return {
            ...state,
            changeSurveyStatus: initialState.changeSurveyStatus,
          };
        }

        case 'clickedSearchDeliveryTarget':
          return {
            ...state,
            clickedSearchDeliveryTarget: initialState.clickedSearchDeliveryTarget,
          };

        case 'clickedGoToApply':
          return {
            ...state,
            clickedGoToApply: initialState.clickedGoToApply,
          };

        case 'all':
        default:
          return initialState;
      }
    },

    /**
     * バリデーション結果がvalidかinvalidかを更新する。
     * コンポーネントから必要なstateをpayloadで渡す。
     */
    validateOnClickedSearchDeliveryTarget: (state, action) => {
      const validationErrors = validateClickedSearchDeliveryTarget(action.payload);

      // ひとつでもエラーがあればinvalid;
      const invalid = isInvalid(validationErrors);

      return {
        ...state,
        clickedSearchDeliveryTarget: {
          ...state.clickedSearchDeliveryTarget,
          status: {
            valid: !invalid,
            invalid,
          },
          errors: validationErrors,
        },
      };
    },

    /**
     * 申し込みボタンがクリックされた時のバリデーション用。
     * コンポーネントから必要なstateをpayloadで渡す。
     */
    validateClickedGoToApply: (state, action) => {
      const validationErrors = validateClickGoToApply(action.payload);

      // ひとつでもエラーがあればinvalid;
      const invalid = isInvalid(validationErrors);

      return {
        ...state,
        clickedGoToApply: {
          ...state.clickedGoToApply,
          status: {
            valid: !invalid,
            invalid,
            // revalidateClickedGoToApplyと区別するため、trueにする。
            isSubmitBtnClicked: true,
          },
          errors: validationErrors,
        },
      };
    },

    /**
     * validateClickedGoToApplyでエラーが表示された後に、再度inputの値が変更された時に、
     * 再度validationを行うためのaction。
     *
     * ↑のvalidateClickedGoToApplyで行うと、statusがvalidになった瞬間に次のページに遷移してしまうため、
     * こちらを新たに作成。
     */
    revalidateClickedGoToApply: (state, action) => {
      const validationErrors = validateClickGoToApply(action.payload);

      // ひとつでもエラーがあればinvalid;
      const invalid = isInvalid(validationErrors);

      return {
        ...state,
        clickedGoToApply: {
          ...state.clickedGoToApply,
          status: {
            valid: !invalid,
            invalid,
            // submit時と区別するため、falseにする。
            isSubmitBtnClicked: false,
          },
          errors: validationErrors,
        },
      };
    },

    clickedGoToApplyStatusUpdate: (state, action) => {
      return {
        ...state,
        clickedGoToApply: {
          ...state.clickedGoToApply,
          status: {
            ...state.clickedGoToApply.status,
            ...action.payload,
          },
        },
      };
    },

    validateSurveyStatusChanged: (state, action) => {
      const validationErrors = validateSurveyStatusChange(action.payload);
      // ひとつでもエラーがあればinvalid;
      const invalid = isInvalid(validationErrors);
      return {
        ...state,
        changeSurveyStatus: {
          ...state.changeSurveyStatus,
          status: {
            valid: !invalid,
            invalid,
          },
          errors: validationErrors,
        },
      };
    },
  },

  // APIのstatusがエラーの場合は、authentication.invalidにtrueをセットする。
  extraReducers: {
    [registerUser.fulfilled.type]: (state, action) => {
      if (action.payload.status !== GMOASK_STATUS.OK) {
        return {
          ...state,
          signup: {
            errors: {
              invalid: true,
              message: i18n.t('validation.signupError'),
            },
          },
        };
      }
      return {
        ...state,
        signup: {
          errors: {
            invalid: false,
            message: '',
          },
        },
      };
    },
    [registerAdmin.fulfilled.type]: (state, action) => {
      if (action.payload.status !== GMOASK_STATUS.OK) {
        return {
          ...state,
          signup: {
            errors: {
              invalid: true,
              message: i18n.t('validation.signupError'),
            },
          },
        };
      }
      return {
        ...state,
        signup: {
          errors: {
            invalid: false,
            message: '',
          },
        },
      };
    },
    [signinUser.fulfilled.type]: (state, action) => {
      // エラーメッセージを表示すべきエラーステータスならメッセージをstoreにセット
      if (
        GMOASK_STATUS.VALIDATION_SHOW_MESSAGE_MIN < action.payload.status &&
        action.payload.status < GMOASK_STATUS.VALIDATION_SHOW_MESSAGE_MAX
      ) {
        return {
          ...state,
          signin: {
            errors: {
              authentication: {
                invalid: true,
                message: ERROR_CODE_MESSAGE_MAP[action.payload.status],
              },
            },
          },
        };
      }

      return {
        ...state,
        signin: {
          errors: {
            authentication: {
              invalid: false,
              message: '',
            },
          },
        },
      };
    },

    [signinAdmin.fulfilled.type]: (state, action) => {
      // エラーメッセージを表示すべきエラーステータスならメッセージをstoreにセット
      if (
        GMOASK_STATUS.VALIDATION_SHOW_MESSAGE_MIN < action.payload.status &&
        action.payload.status < GMOASK_STATUS.VALIDATION_SHOW_MESSAGE_MAX
      ) {
        return {
          ...state,
          signin: {
            errors: {
              authentication: {
                invalid: true,
                message: ERROR_CODE_MESSAGE_MAP[action.payload.status],
              },
            },
          },
        };
      }

      return {
        ...state,
        signin: {
          errors: {
            authentication: {
              invalid: false,
              message: '',
            },
          },
        },
      };
    },

    [logoutAdmin.fulfilled.type]: () => {
      return initialState;
    },

    [logoutUser.fulfilled.type]: () => {
      return initialState;
    },
  },
});

export const {
  validationInitialized,
  validateSurveyStatusChanged,
  validateClickedGoToApply,
  revalidateClickedGoToApply,
  clickedGoToApplyStatusUpdate,
  validateOnClickedSearchDeliveryTarget,
} = validationSlice.actions;

// reducerをexportする
export default validationSlice.reducer;
